import { useState, useEffect, useCallback } from 'react';
import { Project, ProjectCreate, ProjectWithSlideCount } from '../types';
import { getProjects, createProject, deleteProject } from '../services/projects';

export function useProjects() {
  const [projects, setProjects] = useState<ProjectWithSlideCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedProjects = await getProjects();
      setProjects(fetchedProjects);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch projects'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (projectData: ProjectCreate) => {
    setLoading(true);
    try {
      const newProject = await createProject(projectData);
      const projectWithSlideCount = { ...newProject, slideCount: newProject.slides?.length || 0 } as ProjectWithSlideCount;
      setProjects(prevProjects => [projectWithSlideCount, ...prevProjects]);
      setError(null);
      return newProject;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create project'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (id: string) => {
    try {
      await deleteProject(id);
      setProjects(prevProjects => prevProjects.filter(project => project._id !== id));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete project'));
      throw err;
    }
  };

  const handleUpdateProjectLocal = (id: string, updates: Partial<ProjectWithSlideCount>) => {
    setProjects(prevProjects => 
      prevProjects.map(project => 
        project._id === id ? { ...project, ...updates } : project
      )
    );
  };

  return {
    projects,
    loading,
    error,
    fetchProjects,
    handleCreateProject,
    handleDeleteProject,
    handleUpdateProjectLocal,
  };
} 